import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { ImageContainerProps } from '../../types/imageTransform';
import { getTransformStyle } from '../../utils/imageTransform';
import { OCR_GUIDE_ROW_HEIGHT } from './AuditDynamicFormConstants';

const ImageContainer = forwardRef<HTMLDivElement, ImageContainerProps & { children?: React.ReactNode }>(({
  imageUrl,
  transform,
  isDragging,
  onDoubleClick,
  onMouseDown,
  hoveredRowIndex,
  onRowHover,
  onGuideLineClientYChange,
  showHoverGuideLine = true,
  showGuideGrid = true,
  guideGridOriginClientY,
  ticketCount,
  children,
}, ref) => {

  const highlightColor = '#000000';
  const GRID_TOP_OFFSET = 0;
  const ROW_HEIGHT = OCR_GUIDE_ROW_HEIGHT;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerTop, setContainerTop] = useState<number | null>(null);
  const guideGridOffsetTop = guideGridOriginClientY != null && containerTop != null
    ? guideGridOriginClientY - containerTop
    : GRID_TOP_OFFSET;
  const normalizedGuideGridOffsetTop = ((guideGridOffsetTop % ROW_HEIGHT) + ROW_HEIGHT) % ROW_HEIGHT;
  const hoverGuideLineTop = guideGridOriginClientY != null
    ? guideGridOffsetTop + ((hoveredRowIndex ?? 1) - 1) * ROW_HEIGHT
    : (hoveredRowIndex ?? 0) * ROW_HEIGHT + GRID_TOP_OFFSET;

  const setRefs = useCallback((element: HTMLDivElement | null) => {
    containerRef.current = element;
    if (typeof ref === 'function') {
      ref(element);
    } else if (ref) {
      ref.current = element;
    }
  }, [ref]);

  const updateContainerTop = useCallback(() => {
    const nextTop = containerRef.current?.getBoundingClientRect().top ?? null;
    setContainerTop(nextTop);
  }, []);

  useEffect(() => {
    updateContainerTop();
    window.addEventListener('resize', updateContainerTop);
    window.addEventListener('scroll', updateContainerTop, true);
    return () => {
      window.removeEventListener('resize', updateContainerTop);
      window.removeEventListener('scroll', updateContainerTop, true);
    };
  }, [guideGridOriginClientY, imageUrl, updateContainerTop]);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!onRowHover || !ticketCount) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const mouseY = e.clientY - rect.top;

    if (guideGridOriginClientY != null) {
      const gridOriginY = guideGridOriginClientY - rect.top;
      const boundaryIndex = Math.round((mouseY - gridOriginY) / ROW_HEIGHT);
      const lineOffsetY = gridOriginY + (boundaryIndex * ROW_HEIGHT);

      if (lineOffsetY < 0 || lineOffsetY > rect.height) {
        onRowHover(null);
        onGuideLineClientYChange?.(null);
        return;
      }

      onRowHover(boundaryIndex + 1);
      onGuideLineClientYChange?.(rect.top + lineOffsetY);
      return;
    }

    // Account for the grid offset: grid starts at 166px from container top
    const mouseYRelativeToGrid = mouseY - GRID_TOP_OFFSET;

    // Only calculate row if mouse is within the grid area
    if (mouseYRelativeToGrid < 0) {
      onRowHover(null);
      onGuideLineClientYChange?.(null);
      return;
    }

    // Calculate which row (0-based) based on 27px row height
    const rowIndex = Math.floor(mouseYRelativeToGrid / ROW_HEIGHT);

    // Convert to 1-based index (matching form behavior where idx + 1 is used)
    // The form uses fields.length for the add button, which equals ticketCount
    if (rowIndex >= 0 && rowIndex < ticketCount) {
      // Regular ticket rows: convert 0-based to 1-based
      onRowHover(rowIndex + 1);
      onGuideLineClientYChange?.(rect.top + ((rowIndex + 1) * ROW_HEIGHT) + GRID_TOP_OFFSET);
    } else if (rowIndex === ticketCount) {
      // Add button area: use ticketCount (matches form's fields.length)
      onRowHover(ticketCount);
      onGuideLineClientYChange?.(rect.top + (ticketCount * ROW_HEIGHT) + GRID_TOP_OFFSET);
    } else {
      // Outside valid range
      onRowHover(null);
      onGuideLineClientYChange?.(null);
    }
  };

  const handleMouseLeave = () => {
    if (onRowHover) {
      onRowHover(null);
    }
    onGuideLineClientYChange?.(null);
  };

  return (
    <Box
      ref={setRefs}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      sx={{
        flex: 1,
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        minHeight: 0,
      }}
    >
      {/* Image */}
      <Box
        component="img"
        src={imageUrl}
        alt="Receipt"
        sx={{
          // Remove width constraints - let transform scale handle sizing
          display: 'block',
          transform: getTransformStyle(transform),
          transformOrigin: `${-transform.position.x}px ${-transform.position.y}px`,
          willChange: 'transform',
          transition: isDragging ? 'none' : 'transform 0.2s cubic-bezier(0.4,0,0.2,1)',
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
        }}
        onDoubleClick={onDoubleClick}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={onMouseDown}
      />


      {/* Grid Overlay */}
      {showGuideGrid && (
        <Box
          sx={{
            position: 'absolute',
            top: `${GRID_TOP_OFFSET}px`,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: 'none',
            backgroundImage: `linear-gradient(to bottom, #A3AED099 2px, transparent 2px)`,
            backgroundSize: `100% ${ROW_HEIGHT}px`,
            backgroundPosition: `0 ${normalizedGuideGridOffsetTop}px`,
            opacity: 1,
            zIndex: 1,
          }}
          onClick={(e) => e.stopPropagation()}
        />
      )}

      {/* Highlighted Grid Line Overlay */}
      {showHoverGuideLine && hoveredRowIndex !== null && hoveredRowIndex !== undefined && (
        <Box
          sx={{
            position: 'absolute',
            top: `${hoverGuideLineTop}px`,
            left: 0,
            right: 0,
            height: '2px',
            backgroundColor: highlightColor,
            pointerEvents: 'none',
            zIndex: 5,
            boxShadow: '0 0 8px rgba(255, 107, 53, 0.6)',
            // transition: 'all 0.1s ease-in-out',
          }}
          onClick={(e) => e.stopPropagation()}
        />
      )}

      {/* Info */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 8,
          left: 8,
          bgcolor: 'rgba(0, 0, 0, 0.7)',
          color: 'white',
          px: 1,
          py: 0.5,
          borderRadius: 1,
          fontSize: '0.75rem',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {Math.round(transform.scale * 100)}% | {transform.rotation}° | Drag to pan | Double-click to toggle zoom
      </Box>
    </Box>
  );
});

export default ImageContainer;
